/**
 * advanceHelpers — Phase 5.3 Track B handler primitives.
 *
 * Composable helpers for the per-step handlers in `advanceDispatch.ts`.
 * Each primitive captures one shape of "advance to next step" mutation
 * so handler bodies shrink from 6–8 LOC inline to 1–2 LOC composition.
 *
 * The first primitive shipped (PR 5.3.a) is `advanceStep`, which covers
 * "advance to a named next step with a single point either set as the
 * sole entry in `session.points` or appended to the existing list".
 * Subsequent PRs (5.3.b / 5.3.c / ...) add more primitives.
 */

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import type {
  AdvanceResult,
  AdvanceWithTextContext,
  DraftPoint,
  DraftSession,
  DraftStep,
} from "../draftTypes";

export interface AdvanceStepOptions {
  /**
   * How to fold `point` into `session.points`:
   *   - `"set"` (default for first-point handlers): replace the list
   *     with `[point]`.
   *   - `"append"` (default for next-point handlers): push onto the
   *     existing `session.points` tail.
   */
  mode?: "set" | "append";
  /**
   * Optional `closed` override. When provided, the next session's
   * `closed` flag is set to this value; otherwise `closed` is
   * inherited from `session` (no-op).
   */
  closed?: boolean;
}

/**
 * Advance to `nextStep` while folding `point` into `session.points` per
 * the `mode` option. Always resets `hoverPoint` to `null` (matches the
 * legacy inline shape — handlers consistently clear hover on advance).
 */
export function advanceStep(
  session: DraftSession,
  point: DraftPoint,
  nextStep: DraftStep,
  options: AdvanceStepOptions = {},
): AdvanceResult {
  const mode = options.mode ?? "set";
  const nextPoints =
    mode === "append" ? [...session.points, point] : [point];
  return {
    nextSession: {
      ...session,
      step: nextStep,
      points: nextPoints,
      hoverPoint: null,
      ...(options.closed !== undefined ? { closed: options.closed } : {}),
    },
  };
}

/**
 * Branch on a `session.options[key]` value. When the option equals
 * `value`, advance to `matchStep`; otherwise advance to `defaultStep`.
 * Both branches use the `advanceStep` default mode (`points: [point]`),
 * `hoverPoint: null`. Used by step handlers like `circle-center` that
 * select between 2-point / 3-point construction modes via a session
 * option flag.
 */
export function branchByOption(
  session: DraftSession,
  point: DraftPoint,
  optionKey: string,
  optionValue: string,
  matchStep: DraftStep,
  defaultStep: DraftStep,
): AdvanceResult {
  const nextStep =
    session.options[optionKey] === optionValue ? matchStep : defaultStep;
  return advanceStep(session, point, nextStep);
}

/**
 * Extract a scalar value from a draft text input. Recognises:
 *   - plain number ("12.5")
 *   - @-prefixed relative ("@12.5") → strip prefix
 *   - polar form ("12.5<45") → use length component only
 *   - 2D form ("12.5,3") → use hypot
 *
 * Returns `null` when the text is empty or parses to NaN.
 *
 * Moved from `advanceDispatch.ts` so the `parseScalarAndAdvance`
 * primitive can use it without forming a cycle back into the
 * dispatcher.
 */
export function extractScalarFromDraftText(rawText: string): number | null {
  const text = rawText.trim();
  if (text === "") return null;

  const ltIdx = text.indexOf("<");
  if (ltIdx >= 0) {
    const head = text.slice(0, ltIdx).replace(/^[@#]/, "");
    const value = Number(head);
    if (Number.isFinite(value)) return value;
    return null;
  }

  const commaIdx = text.indexOf(",");
  if (commaIdx >= 0) {
    const head = text.slice(0, commaIdx).replace(/^[@#]/, "");
    const tail = text.slice(commaIdx + 1);
    const dx = Number(head);
    const dy = Number(tail);
    if (Number.isFinite(dx) && Number.isFinite(dy)) {
      return Math.hypot(dx, dy);
    }
    return null;
  }

  const stripped = text.replace(/^[@#]/, "");
  const value = Number(stripped);
  return Number.isFinite(value) ? value : null;
}

export interface ParseScalarAndAdvanceOptions {
  /** Key in `session.options` to read the cached default + write the new value. */
  optionKey: string;
  /** Default value used when `ctx.text` is empty. */
  defaultValue: string;
  /** Next step to advance to on success. */
  nextStep: DraftStep;
  /**
   * Validator + transformer. Receives the parsed scalar, returns
   * either the canonical numeric value to store (which may be
   * floored, clamped, etc.) or `null` to reject the input
   * (`nextSession: session` — no advance).
   */
  validate?: (value: number) => number | null;
}

/**
 * Text-handler primitive: parse a scalar from `ctx.text` (or fall back
 * to the cached default in `session.options[optionKey]` when text is
 * empty), validate via optional `validate`, store the canonical value
 * back into `session.options[optionKey]`, and advance to `nextStep`.
 * Returns `{ nextSession: session }` (no advance) when the parse or
 * validator rejects the input.
 */
export function parseScalarAndAdvance(
  session: DraftSession,
  ctx: AdvanceWithTextContext,
  options: ParseScalarAndAdvanceOptions,
): AdvanceResult {
  const { optionKey, defaultValue, nextStep, validate } = options;
  const cached = session.options[optionKey] ?? defaultValue;
  const sourceText = ctx.text === "" ? cached : ctx.text;
  const rawValue = extractScalarFromDraftText(sourceText);
  if (rawValue === null) {
    return { nextSession: session };
  }
  const validated = validate ? validate(rawValue) : rawValue;
  if (validated === null) {
    return { nextSession: session };
  }
  return {
    nextSession: {
      ...session,
      step: nextStep,
      hoverPoint: null,
      options: { ...session.options, [optionKey]: String(validated) },
    },
  };
}

/**
 * Commit a single geometry command and end the draft session.
 *
 * Equivalent to the inline shape:
 * ```
 * {
 *   nextSession: null,
 *   commit: { geometryCommand, summary, commandText },
 * }
 * ```
 *
 * Used by the ~25 % of point handlers that produce one geometry
 * mutation and terminate the session (CIRCLE radius, POINT, RECTANGLE
 * second-corner, REVCLOUD second-corner, etc.).
 */
export function commitGeometry(
  geometryCommand: EditorCommand,
  summary: string,
  commandText: string,
): AdvanceResult {
  return {
    nextSession: null,
    commit: { geometryCommand, summary, commandText },
  };
}
